/**
 * features/finance/voucherWrite.ts
 * ------------------------------------------------------------------
 * Raising a voucher, and asking the server to move it.
 *
 * THE SPLIT IS THE POINT. Drafting is a client write; everything after it is
 * a callable. This file does not decide who may approve anything — it cannot,
 * because a decision made in a browser is a decision an attacker can skip.
 * It drafts, it submits, and for every other transition it asks
 * functions/vouchers.js and reports back what the server said.
 *
 * THE SCREEN STILL CHECKS FIRST, using the same pure module the server
 * mirrors. Not as a control — as manners. A bursar should be told "you
 * raised this, somebody else has to approve it" before they click, not
 * after a round trip.
 * ------------------------------------------------------------------ */
import { collection, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, setDoc, updateDoc } from '@/lib/firebase';
import { auditFromContext } from '@/lib/audit';
import { round2 } from '@/lib/financeCore';
import {
  VOUCHER_COLLECTIONS, linesProblem, voucherActionProblem,
  type Voucher, type VoucherAction, type VoucherActor, type VoucherLine,
  type VoucherSourceType, type PaymentMethodOut,
} from '@/lib/vouchers';

export interface Actor {
  uid?: string;
  email?: string;
  displayName?: string;
}

const nowIso = () => new Date().toISOString();
const COL = VOUCHER_COLLECTIONS.vouchers;

export interface NewVoucherInput {
  fy: string;
  source: { type: VoucherSourceType; id?: string };
  payeeName: string;
  supplierId?: string;
  staffId?: string;
  lines: VoucherLine[];
  narration: string;
  /** The supplier's own invoice number — their only usable reference. */
  supplierInvoiceNo?: string;
  dueDate?: string;
  paymentRef: string;
  method: PaymentMethodOut;
  payableAccountKey?: string;
}

/** Ask the server for the next number in the year's gapless series. */
async function nextNumber(fy: string): Promise<{ voucherNo: string }> {
  const fn = httpsCallable<{ fy: string }, { voucherNo: string }>(functions, 'nextVoucherNumber');
  const r = await fn({ fy });
  return r.data;
}

/**
 * Create a draft.
 *
 * The number is allocated by the server even for a draft, so a voucher has
 * one identity from the moment it exists. Numbering at submission instead
 * would mean the thing somebody has been discussing all morning changes its
 * name when they finally send it.
 */
export async function createVoucher(input: NewVoucherInput, actor: Actor): Promise<Voucher> {
  const total = round2((input.lines ?? []).reduce((a, l) => a + (Number(l.amount) || 0), 0));
  const problem = linesProblem({ ...input, total });
  if (problem) throw new Error(problem);

  const { voucherNo } = await nextNumber(input.fy);
  const ref = doc(collection(db, COL));
  const record: Voucher = {
    id: ref.id,
    voucherNo,
    fy: input.fy,
    source: input.source,
    payeeName: input.payeeName.trim(),
    ...(input.supplierId ? { supplierId: input.supplierId } : {}),
    ...(input.staffId ? { staffId: input.staffId } : {}),
    lines: input.lines.map((l) => ({
      description: l.description.trim(),
      accountCode: l.accountCode.trim(),
      amount: round2(Number(l.amount) || 0),
    })),
    total,
    narration: input.narration.trim(),
    ...(input.supplierInvoiceNo?.trim() ? { supplierInvoiceNo: input.supplierInvoiceNo.trim() } : {}),
    ...(input.dueDate ? { dueDate: input.dueDate } : {}),
    paymentRef: input.paymentRef.trim().slice(0, 40),
    method: input.method,
    currentStatus: 'draft',
    preparedBy: actor.uid ?? '',
    preparedByName: actor.displayName ?? actor.email ?? '',
    preparedAt: nowIso(),
    raisesAccrual: true,
    payableAccountKey: input.payableAccountKey ?? 'AP_TRADE',
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };

  await setDoc(ref, record);
  auditFromContext(actor as never, 'voucher_drafted', 'finance', {
    targetId: ref.id,
    targetType: 'journal',
    targetName: voucherNo,
    details: { payee: record.payeeName, total, method: record.method },
  });
  return record;
}

/** Edit a draft. The rules allow this only to whoever raised it. */
export async function updateDraft(
  voucherId: string,
  patch: Partial<NewVoucherInput>,
): Promise<void> {
  const ref = doc(db, COL, voucherId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('That voucher no longer exists.');
  const cur = snap.data() as Voucher;
  if (cur.currentStatus !== 'draft') {
    throw new Error('Only a draft can be edited. Reject it back to draft to change it.');
  }

  const lines = (patch.lines ?? cur.lines).map((l) => ({
    description: l.description.trim(),
    accountCode: l.accountCode.trim(),
    amount: round2(Number(l.amount) || 0),
  }));
  const total = round2(lines.reduce((a, l) => a + l.amount, 0));
  const merged = { ...cur, ...patch, lines, total };
  const problem = linesProblem(merged);
  if (problem) throw new Error(problem);

  await updateDoc(ref, {
    ...(patch.payeeName !== undefined ? { payeeName: patch.payeeName.trim() } : {}),
    ...(patch.supplierId !== undefined ? { supplierId: patch.supplierId } : {}),
    ...(patch.narration !== undefined ? { narration: patch.narration.trim() } : {}),
    ...(patch.dueDate !== undefined ? { dueDate: patch.dueDate } : {}),
    ...(patch.paymentRef !== undefined ? { paymentRef: patch.paymentRef.trim().slice(0, 40) } : {}),
    ...(patch.method !== undefined ? { method: patch.method } : {}),
    lines,
    total,
    updatedAt: nowIso(),
  });
}

/**
 * Submit a draft for checking.
 *
 * The only transition the client makes, and it is safe to: it moves a
 * voucher from something only its author can see into the queue, and it
 * grants nothing. Everything past this point commits money.
 */
export async function submitVoucher(voucherId: string, actor: Actor & { can: VoucherActor }): Promise<void> {
  const ref = doc(db, COL, voucherId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('That voucher no longer exists.');
  const v = { id: snap.id, ...snap.data() } as Voucher;

  const problem = voucherActionProblem(v, 'submit', actor.can);
  if (problem) throw new Error(problem);

  await updateDoc(ref, { currentStatus: 'submitted', updatedAt: nowIso() });
  auditFromContext(actor as never, 'voucher_submitted', 'finance', {
    targetId: voucherId,
    targetType: 'journal',
    targetName: v.voucherNo,
    details: { total: v.total, payee: v.payeeName },
  });
}

/* ------------------------- the server transitions ------------------------- */

const call = <T extends object>(name: string) =>
  httpsCallable<T, { id: string; voucherNo: string; from: string; to: string }>(functions, name);

/**
 * Ask the server to move a voucher.
 *
 * The error is surfaced verbatim. The server's refusals are written as
 * sentences a bursar can act on — "you checked this voucher, so somebody
 * else has to approve it" — and rewording them here would lose the only
 * part of a permission failure that is useful.
 */
async function serverTransition(
  name: string,
  payload: Record<string, unknown>,
): Promise<void> {
  try {
    await call(name)(payload as never);
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    throw new Error(message.replace(/^FirebaseError:\s*/i, ''));
  }
}

export const checkVoucher = (voucherId: string, note?: string) =>
  serverTransition('checkVoucher', { voucherId, note });

export const approveVoucher = (voucherId: string, note?: string) =>
  serverTransition('approveVoucher', { voucherId, note });

export const payVoucher = (voucherId: string, bankRef?: string, note?: string) =>
  serverTransition('payVoucher', { voucherId, bankRef, note });

export const rejectVoucher = (voucherId: string, reason: string) =>
  serverTransition('rejectVoucher', { voucherId, reason });

export const cancelVoucher = (voucherId: string, reason?: string) =>
  serverTransition('cancelVoucher', { voucherId, reason });

/** Which server call an action maps to. */
export function runAction(
  action: VoucherAction,
  voucherId: string,
  opts: { note?: string; reason?: string; bankRef?: string } = {},
): Promise<void> {
  switch (action) {
    case 'check': return checkVoucher(voucherId, opts.note);
    case 'approve': return approveVoucher(voucherId, opts.note);
    case 'pay': return payVoucher(voucherId, opts.bankRef, opts.note);
    case 'reject': return rejectVoucher(voucherId, opts.reason ?? '');
    case 'cancel': return cancelVoucher(voucherId, opts.reason);
    default: return Promise.reject(new Error('That action is not a server transition.'));
  }
}
